import { computed, ref } from 'vue';
import { isAxiosError } from 'axios';
import { useModal } from 'vue-final-modal';
import { ConfirmDeleteModal } from '@/shared/ui';
import { useCustomToast } from './useCustomToast';
import type { BulkDeleteConfig, BulkDeleteResult } from './types/bulk-delete.types';

export const BULK_DELETE_MAX_ITEMS = 25;

const BULK_DELETE_INVALID_IDS_MESSAGE = 'IDs inválidos fornecidos';

function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function createSingleDeleteResult(id: number): BulkDeleteResult {
  return {
    totalRequested: 1,
    successCount: 1,
    failureCount: 0,
    succeededIds: [id],
    failedOperations: [],
    invalidIds: [],
  };
}

function buildFailureMessage(failedOperations: Array<{ id: number; error: string }>): string {
  if (failedOperations.length === 0) return '';
  const uniqueErrors = [...new Set(failedOperations.map((op) => op.error))];
  if (uniqueErrors.length === 1 && failedOperations.length > 1) {
    const ids = failedOperations.map((op) => op.id).join(', ');
    return `IDs ${ids}: ${uniqueErrors[0]}`;
  }
  return failedOperations.map((op) => `ID ${op.id}: ${op.error}`).join('; ');
}

export function useBulkDelete<T extends { id: number }>(config: BulkDeleteConfig<T>) {
  const toast = useCustomToast();
  const isFemale = config.entityName.gender === 'female';
  const agree = (male: string, female: string) => (isFemale ? female : male);
  const modalDescription = ref<string>('A ação não pode ser desfeita.');

  const showDeleteSuccessToast = (count: number) => {
    const message =
      count === 1
        ? (config.successMessage ??
          `${capitalize(config.entityName.singular)} ${agree('excluído', 'excluída')} com sucesso.`)
        : (config.successMessagePlural ??
          `${count} ${config.entityName.plural} ${agree('excluídos', 'excluídas')} com sucesso.`);

    toast.showSuccessToast({
      title: message,
      type: 'success',
    });
  };

  const showDeleteErrorToast = (count: number) => {
    const message =
      count === 1
        ? (config.errorMessage ?? `Erro ao excluir ${config.entityName.singular}. Tente novamente.`)
        : (config.errorMessagePlural ??
          `Erro ao excluir ${config.entityName.plural}. Tente novamente.`);

    toast.showErrorToast({
      title: message,
      type: 'error',
    });
  };

  const isSelectionValid = (ids: number[]): boolean => {
    if (!config.validateSelection) return true;
    const validation = config.validateSelection(ids);
    if (!validation.allowed) {
      toast.showErrorToast({
        title: validation.message ?? 'Operação não permitida.',
        type: 'error',
      });
      return false;
    }
    return true;
  };

  const executeDelete = async (ids: number[]): Promise<BulkDeleteResult> => {
    if (ids.length === 1 && config.singleDeleteApi) {
      await config.singleDeleteApi(ids[0]);
      return createSingleDeleteResult(ids[0]);
    }
    const response = await config.bulkDeleteApi(ids);
    return response.data;
  };

  const notifySuccess = (result: BulkDeleteResult) => {
    config.onDeleteSuccess(result.succeededIds);
    showDeleteSuccessToast(result.successCount);
    config.removeFromSelection(result.succeededIds);
  };

  const notifyFailures = (result: BulkDeleteResult) => {
    const attemptedCount = result.successCount + result.failureCount;
    const entityText = attemptedCount === 1 ? config.entityName.singular : config.entityName.plural;
    const countText =
      result.failureCount < attemptedCount
        ? `${result.failureCount} de ${attemptedCount}`
        : String(result.failureCount);
    const verbText =
      result.failureCount === 1
        ? `não pôde ser ${agree('excluído', 'excluída')}`
        : `não puderam ser ${agree('excluídos', 'excluídas')}`;
    toast.showErrorToast({
      title: `${countText} ${entityText} ${verbText}`,
      message: buildFailureMessage(result.failedOperations),
      type: 'error',
    });
  };

  const notifyMissing = (count: number) => {
    const entityText = count === 1 ? config.entityName.singular : config.entityName.plural;
    toast.showErrorToast({
      title:
        count === 1
          ? `1 ${entityText} não ${agree('encontrado', 'encontrada')}`
          : `${count} ${entityText} não ${agree('encontrados', 'encontradas')}`,
      type: 'error',
    });
  };

  /**
   * Ids the server no longer knows about can never succeed on a retry, so they
   * leave the selection and the caller is told the list is out of date.
   */
  const discardMissingIds = (missingIds: number[], shouldRefresh: boolean) => {
    config.removeFromSelection(missingIds);
    notifyMissing(missingIds.length);
    if (shouldRefresh) config.onStaleData?.();
  };

  const processDeleteResult = (result: BulkDeleteResult) => {
    if (result.successCount > 0) notifySuccess(result);
    if (result.failureCount > 0) notifyFailures(result);
    if (result.invalidIds.length > 0) {
      discardMissingIds(result.invalidIds, result.successCount === 0);
    }
  };

  const handleDeleteRequestError = (error: unknown, attemptedIds: number[]) => {
    const status = isAxiosError(error) ? error.response?.status : undefined;
    const serverMessage = isAxiosError(error) ? error.response?.data?.message : undefined;

    const isMissingSingle = status === 404 && attemptedIds.length === 1 && !!config.singleDeleteApi;
    if (isMissingSingle || (status === 400 && serverMessage === BULK_DELETE_INVALID_IDS_MESSAGE)) {
      discardMissingIds(attemptedIds, true);
      return;
    }

    if ((status === 400 || status === 403) && serverMessage) {
      toast.showErrorToast({
        title: serverMessage,
        type: 'error',
      });
    } else {
      showDeleteErrorToast(attemptedIds.length);
    }
  };

  const handleBulkDelete = async () => {
    closeDeleteConfirmation();

    const ids = config.selectedIds();
    if (!isSelectionValid(ids)) return;

    try {
      const result = await executeDelete(ids);
      processDeleteResult(result);
    } catch (error: unknown) {
      handleDeleteRequestError(error, ids);
    }
  };

  const modalTitle = computed(() => {
    const count = config.selectedCount();
    if (config.getConfirmTitle) {
      return config.getConfirmTitle(count);
    }
    if (count <= 1) {
      return `Tem certeza que deseja excluir ${agree('o', 'a')} ${config.entityName.singular}?`;
    }
    return `Tem certeza que deseja excluir ${count} ${config.entityName.plural}?`;
  });

  const { open: openDeleteConfirmation, close: closeDeleteConfirmation } = useModal({
    component: ConfirmDeleteModal,
    attrs: {
      get title() {
        return modalTitle.value;
      },
      get description() {
        return modalDescription.value;
      },
      onClose: () => {
        closeDeleteConfirmation();
        if (config.onModalClose) {
          config.onModalClose();
        }
      },
      onConfirm: () => handleBulkDelete(),
    },
  });

  const exceedsMaxItems = (ids: number[]): boolean => {
    if (ids.length <= BULK_DELETE_MAX_ITEMS) return false;
    toast.showErrorToast({
      title: `Selecione no máximo ${BULK_DELETE_MAX_ITEMS} ${config.entityName.plural} por vez`,
      message: `Você selecionou ${ids.length}. Desmarque alguns e tente novamente.`,
      type: 'error',
    });
    return true;
  };

  let latestOpenRequest = 0;

  const openDeleteConfirmationModal = async () => {
    if (exceedsMaxItems(config.selectedIds())) return;

    const request = ++latestOpenRequest;
    const defaultDescription = 'A ação não pode ser desfeita.';
    modalDescription.value = defaultDescription;

    if (config.getDeleteDescription) {
      try {
        const ids = config.selectedIds();
        const customDescription = await config.getDeleteDescription(ids);
        if (request !== latestOpenRequest) return;
        modalDescription.value = customDescription;
      } catch (error) {
        console.error('Erro ao obter descrição personalizada:', error);
      }
      if (request !== latestOpenRequest) return;
    }

    openDeleteConfirmation();
  };

  const showNoSelectionError = () => {
    return toast.showErrorToast({
      title: `${agree('Nenhum', 'Nenhuma')} ${config.entityName.singular} ${agree('selecionado', 'selecionada')}`,
      message: `Selecione pelo menos ${agree('um', 'uma')} ${config.entityName.singular} para excluir.`,
      type: 'error',
    });
  };

  return {
    handleBulkDelete,
    openDeleteConfirmationModal,
    showNoSelectionError,
    showDeleteSuccessToast,
    showDeleteErrorToast,
  };
}
